import React, { useState, useRef, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { useAuth } from '@/contexts/auth';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Grid2X2, Package, Users, ShoppingBag, Settings, User2, Upload, Camera, Save, Pencil, X } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { collection, doc, updateDoc, getDoc, getDocs, query, where, limit, startAfter, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import TranslatedText from '@/TranslatedText';
import { ExtendedUser } from '@/contexts/auth/types';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { 
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const AhavexerDashboard: React.FC = () => {
  const { currentUser, userType } = useAuth();
  const extendedUser = currentUser as ExtendedUser | null;
  const [activeTab, setActiveTab] = useState('ahavexers');
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [allAhavexers, setAllAhavexers] = useState<any[]>([]);
  const [isLoadingAhavexers, setIsLoadingAhavexers] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [profileData, setProfileData] = useState({
    ahavexId: '',
    fullName: '',
    email: '',
    phone: '',
    address: '',
    lastLogin: '',
    lastUpdated: '',
    createdAt: '',
    orders: 0,
    customers: 0,
    profileImage: ''
  });

  const formSchema = z.object({
    fullName: z.string().min(2, { message: "Name must be at least 2 characters." }),
    ahavexId: z.string().min(3, { message: "Ahavex ID must be at least 3 characters." }),
    phone: z.string().min(10, { message: "Please enter a valid phone number." }),
    address: z.string().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: profileData.fullName,
      ahavexId: profileData.ahavexId,
      phone: profileData.phone || '',
      address: profileData.address || '',
    },
  });

  useEffect(() => {
    if (profileData) {
      form.reset({
        fullName: profileData.fullName,
        ahavexId: profileData.ahavexId,
        phone: profileData.phone || '',
        address: profileData.address || '',
      });
    }
  }, [profileData, form]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser?.uid) {
        try {
          setIsLoading(true);
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log("Fetched user data:", userData);
            
            setProfileData({
              ahavexId: userData.ahavexId || '',
              fullName: userData.fullName || '',
              email: userData.email || currentUser.email || '',
              phone: userData.phone || '',
              address: userData.address || '',
              lastLogin: userData.lastLogin ? new Date(userData.lastLogin).toLocaleString() : '',
              lastUpdated: userData.lastUpdated ? new Date(userData.lastUpdated).toLocaleString() : '',
              createdAt: userData.createdAt ? new Date(userData.createdAt).toLocaleString() : '',
              orders: userData.orders || 0,
              customers: userData.customers || 0,
              profileImage: userData.profileImage || ''
            });
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          toast({
            title: "Error",
            description: "Failed to load user data",
            variant: "destructive"
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchUserData();
  }, [currentUser]);

  useEffect(() => {
    const fetchAllAhavexers = async () => {
      if (activeTab === 'ahavexers') {
        try {
          setIsLoadingAhavexers(true);
          
          const ahavexersList: any[] = [];
          
          const usersRef = collection(db, 'users');
          
          const baseQuery = query(
            usersRef,
            orderBy('createdAt', 'desc'),
            limit(500)
          );
          
          let currentQuerySnapshot = await getDocs(baseQuery);
          console.log(`Fetched batch of ${currentQuerySnapshot.size} users`);
          
          currentQuerySnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.ahavexId || data.role === 'ahavexer') {
              ahavexersList.push({
                id: doc.id,
                ...data
              });
            }
          });
          
          while (currentQuerySnapshot.size === 500) {
            const lastVisible = currentQuerySnapshot.docs[currentQuerySnapshot.docs.length - 1];
            
            const nextQuery = query(
              usersRef,
              orderBy('createdAt', 'desc'),
              startAfter(lastVisible),
              limit(500)
            );
            
            currentQuerySnapshot = await getDocs(nextQuery);
            console.log(`Fetched additional batch of ${currentQuerySnapshot.size} users`);
            
            currentQuerySnapshot.forEach((doc) => {
              const data = doc.data();
              if (data.ahavexId || data.role === 'ahavexer') {
                ahavexersList.push({
                  id: doc.id,
                  ...data
                });
              }
            });
          }
          
          console.log(`Total ahavexers found: ${ahavexersList.length}`);
          setAllAhavexers(ahavexersList);
        } catch (error) {
          console.error("Error fetching ahavexers:", error);
          toast({
            title: "Error",
            description: "Failed to load ahavexers list",
            variant: "destructive"
          });
        } finally {
          setIsLoadingAhavexers(false);
        }
      }
    };

    fetchAllAhavexers();
  }, [activeTab]);

  if (!currentUser || userType !== 'ahavexer') {
    return <Navigate to="/" replace />;
  }

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      form.reset({
        fullName: profileData.fullName,
        ahavexId: profileData.ahavexId,
        phone: profileData.phone,
        address: profileData.address,
      });
    }
  };

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (!currentUser?.uid) return;
    
    try {
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        fullName: data.fullName,
        ahavexId: data.ahavexId,
        phone: data.phone,
        address: data.address,
        lastUpdated: new Date().toISOString()
      });
      
      setProfileData({
        ...profileData,
        fullName: data.fullName,
        ahavexId: data.ahavexId,
        phone: data.phone,
        address: data.address,
        lastUpdated: new Date().toLocaleString()
      });
      
      setIsEditing(false);
      toast({
        title: "Success",
        description: "Profile updated successfully"
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive"
      });
    }
  };

  const compressImage = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          
          let width = img.width;
          let height = img.height;
          const maxDimension = 800;
          
          if (width > height && width > maxDimension) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else if (height > maxDimension) {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressedDataUrl = canvas.toDataURL('image/webp', 0.1);
          resolve(compressedDataUrl);
        };
        
        img.onerror = () => {
          reject(new Error('Error loading image'));
        };
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    if (file.size > 3 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "Image is too large, please select an image less than 3MB",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsUploading(true);
      
      const compressedImage = await compressImage(file);
      
      if (currentUser?.uid) {
        const userRef = doc(db, "users", currentUser.uid);
        await updateDoc(userRef, {
          profileImage: compressedImage,
          lastUpdated: new Date().toISOString()
        });
        
        setProfileData({
          ...profileData,
          profileImage: compressedImage,
          lastUpdated: new Date().toLocaleString()
        });
        
        toast({
          title: "Success",
          description: "Image uploaded successfully"
        });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Error uploading image",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) {
    return (
      <Layout hideSidebar>
        <div className="container mx-auto py-12 text-center">
          <p><TranslatedText text="Loading..." /></p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout hideSidebar>
      <div className="container mx-auto py-6 px-4 md:px-6 max-w-[1400px]">
        <div className="grid grid-cols-1 gap-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-1">
                <div className="space-y-3">
                  <h1 className="text-2xl md:text-3xl font-bold text-ahavex-blue"><TranslatedText text="Ahavexer Portal" /></h1>
                  <p className="text-gray-500"><TranslatedText text="Manage your Ahavex business" /></p>
                  
                  <div className="flex items-center gap-4 pt-3">
                    <div className="relative">
                      <Avatar className="h-16 w-16 rounded-full bg-ahavex-blue">
                        {profileData.profileImage ? (
                          <AvatarImage src={profileData.profileImage} alt={profileData.fullName} />
                        ) : (
                          <AvatarFallback className="text-white text-xl">
                            {profileData.fullName?.charAt(0).toUpperCase() || 'A'}
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <button 
                        onClick={triggerFileInput} 
                        className="absolute bottom-0 right-0 bg-ahavex-blue text-white rounded-full p-1 shadow-lg"
                        title="Update profile picture"
                      >
                        <Camera size={14} />
                      </button>
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileChange} 
                        className="hidden" 
                        accept="image/*"
                      />
                    </div>
                    <div>
                      <p className="font-medium text-lg break-all">{profileData.fullName || profileData.email}</p>
                      <p className="text-gray-500">Ahavexer ID: {profileData.ahavexId}</p>
                      {isUploading && <p className="text-sm text-amber-600"><TranslatedText text="Processing..." /></p>}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="md:col-span-2">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-700">Dashboard Overview</h2>
                    <Button 
                      onClick={handleEditToggle} 
                      size="sm" 
                      variant={isEditing ? "destructive" : "outline"}
                    >
                      {isEditing ? (
                        <>
                          <X className="mr-2 h-4 w-4" />
                          <TranslatedText text="Cancel" />
                        </>
                      ) : (
                        <>
                          <Pencil className="mr-2 h-4 w-4" />
                          <TranslatedText text="Edit Profile" />
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <p className="text-gray-500">Your business at a glance</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 pt-3">
                    <Card className="bg-gray-50 border-none shadow-sm">
                      <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                        <Users className="h-8 w-8 mb-2 text-ahavex-blue" />
                        <p className="text-2xl font-bold">{allAhavexers.length}</p>
                        <p className="text-sm text-gray-500"><TranslatedText text="Ahavexers" /></p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gray-50 border-none shadow-sm">
                      <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                        <ShoppingBag className="h-8 w-8 mb-2 text-ahavex-blue" />
                        <p className="text-2xl font-bold">{profileData.orders}</p>
                        <p className="text-sm text-gray-500"><TranslatedText text="Orders" /></p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gray-50 border-none shadow-sm">
                      <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                        <Users className="h-8 w-8 mb-2 text-ahavex-blue" />
                        <p className="text-2xl font-bold">{profileData.customers}</p>
                        <p className="text-sm text-gray-500"><TranslatedText text="Customers" /></p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gray-50 border-none shadow-sm">
                      <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                        <Grid2X2 className="h-8 w-8 mb-2 text-ahavex-blue" />
                        <p className="text-2xl font-bold">0</p>
                        <p className="text-sm text-gray-500"><TranslatedText text="Revenue" /></p>
                      </CardContent>
                    </Card>
                  </div>

                  {isEditing && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h3 className="text-lg font-medium mb-4"><TranslatedText text="Edit Profile Information" /></h3>
                      <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name="fullName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel><TranslatedText text="Full Name" /></FormLabel>
                                  <FormControl>
                                    <Input placeholder="Enter your full name" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            
                            <FormField
                              control={form.control}
                              name="ahavexId"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel><TranslatedText text="Ahavex ID" /></FormLabel>
                                  <FormControl>
                                    <Input placeholder="Enter your Ahavex ID" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            
                            <FormField
                              control={form.control}
                              name="phone"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel><TranslatedText text="Phone Number" /></FormLabel>
                                  <FormControl>
                                    <Input placeholder="Enter your phone number" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            
                            <FormField
                              control={form.control}
                              name="address"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel><TranslatedText text="Address" /></FormLabel>
                                  <FormControl>
                                    <Input placeholder="Enter your address" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          <div className="flex justify-end">
                            <Button 
                              type="submit" 
                              className="bg-ahavex-blue hover:bg-ahavex-darkblue text-white"
                            >
                              <Save className="mr-2 h-4 w-4" />
                              <TranslatedText text="Save Changes" />
                            </Button>
                          </div>
                        </form>
                      </Form>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="border-b border-gray-100">
                <TabsList className="w-full h-auto p-0 bg-transparent border-b border-gray-100 rounded-none">
                  <TabsTrigger 
                    value="ahavexers" 
                    className="flex-1 py-3 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-ahavex-blue data-[state=active]:text-ahavex-blue data-[state=active]:shadow-none"
                  >
                    <TranslatedText text="Ahavexers" />
                  </TabsTrigger>
                  <TabsTrigger 
                    value="orders" 
                    className="flex-1 py-3 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-ahavex-blue data-[state=active]:text-ahavex-blue data-[state=active]:shadow-none"
                  >
                    <TranslatedText text="Orders" />
                  </TabsTrigger>
                  <TabsTrigger 
                    value="settings" 
                    className="flex-1 py-3 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-ahavex-blue data-[state=active]:text-ahavex-blue data-[state=active]:shadow-none"
                  >
                    <TranslatedText text="Settings" />
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="ahavexers" className="p-6">
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-700"><TranslatedText text="All Ahavexers" /></h3>
                  <p className="text-gray-500"><TranslatedText text="View all registered Ahavexers" /></p>
                  
                  {isLoadingAhavexers ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500"><TranslatedText text="Loading Ahavexers..." /></p>
                    </div>
                  ) : allAhavexers.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-8 text-center">
                      <p className="text-gray-500"><TranslatedText text="No Ahavexers found." /></p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[80px]"><TranslatedText text="Profile" /></TableHead>
                            <TableHead><TranslatedText text="Ahavex ID" /></TableHead>
                            <TableHead><TranslatedText text="Name" /></TableHead>
                            <TableHead><TranslatedText text="Contact" /></TableHead>
                            <TableHead><TranslatedText text="Address" /></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {allAhavexers.map((ahavexer) => (
                            <TableRow key={ahavexer.id}>
                              <TableCell>
                                <Avatar className="h-10 w-10 rounded-full bg-ahavex-blue">
                                  {ahavexer.profileImage ? (
                                    <AvatarImage src={ahavexer.profileImage} alt={ahavexer.fullName} />
                                  ) : (
                                    <AvatarFallback className="text-white text-sm">
                                      {ahavexer.fullName?.charAt(0).toUpperCase() || 'A'}
                                    </AvatarFallback>
                                  )}
                                </Avatar>
                              </TableCell>
                              <TableCell className="font-medium">{ahavexer.ahavexId}</TableCell>
                              <TableCell>
                                <div>{ahavexer.fullName}</div>
                                <div className="text-sm text-gray-500">{ahavexer.email}</div>
                              </TableCell>
                              <TableCell>{ahavexer.phone || '-'}</TableCell>
                              <TableCell>{ahavexer.address || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="orders" className="p-6">
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-700"><TranslatedText text="Recent Orders" /></h3>
                  <p className="text-gray-500"><TranslatedText text="Track and manage your orders" /></p>
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <p className="text-gray-500"><TranslatedText text="No orders found." /></p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="settings" className="p-6">
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-700"><TranslatedText text="Account Settings" /></h3>
                  <p className="text-gray-500"><TranslatedText text="Manage your Ahavexer account settings" /></p>
                  
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-lg font-medium mb-4"><TranslatedText text="Your account information and preferences" /></h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-500"><TranslatedText text="Email" /></p>
                        <p className="text-sm font-semibold">{profileData.email}</p>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-500"><TranslatedText text="Account Type" /></p>
                        <p className="text-sm font-semibold"><TranslatedText text="Ahavexer" /></p>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-500"><TranslatedText text="Ahavex ID" /></p>
                        <p className="text-sm font-semibold">{profileData.ahavexId}</p>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-500"><TranslatedText text="Full Name" /></p>
                        <p className="text-sm font-semibold">{profileData.fullName}</p>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-500"><TranslatedText text="Phone Number" /></p>
                        <p className="text-sm font-semibold">{profileData.phone}</p>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-500"><TranslatedText text="Address" /></p>
                        <p className="text-sm font-semibold">{profileData.address}</p>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-500"><TranslatedText text="Created At" /></p>
                        <p className="text-sm font-semibold">{profileData.createdAt}</p>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-500"><TranslatedText text="Last Login" /></p>
                        <p className="text-sm font-semibold">{profileData.lastLogin}</p>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-500"><TranslatedText text="Last Updated" /></p>
                        <p className="text-sm font-semibold">{profileData.lastUpdated}</p>
                      </div>
                    </div>
                    
                    <div className="mt-6">
                      <p className="text-sm font-medium text-gray-500 mb-2"><TranslatedText text="Profile Image" /></p>
                      <div className="flex items-center gap-4">
                        <Avatar className="h-16 w-16 rounded-full bg-ahavex-blue">
                          {profileData.profileImage ? (
                            <AvatarImage src={profileData.profileImage} alt={profileData.fullName} />
                          ) : (
                            <AvatarFallback className="text-white text-xl">
                              {profileData.fullName?.charAt(0).toUpperCase() || 'A'}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={triggerFileInput}
                          disabled={isUploading}
                        >
                          {isUploading ? (
                            <TranslatedText text="Processing..." />
                          ) : (
                            <>
                              <Upload className="mr-2 h-4 w-4" />
                              <TranslatedText text="Update Profile Picture" />
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        <TranslatedText text="Image size should be less than 3MB" />
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AhavexerDashboard;
